import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApprovalDetail, useApproveReview, useRejectReview } from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  Check,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Circle,
} from 'lucide-react';
import type { ReviewStatus, ReviewLog } from '@saiens/shared/types';

const STATUS_ICONS: Record<ReviewStatus, typeof CheckCircle> = {
  approved: CheckCircle,
  rejected: XCircle,
  pending: Clock,
  waiting: Circle,
};

const STATUS_COLORS: Record<ReviewStatus, string> = {
  approved: 'text-green-500',
  rejected: 'text-red-500',
  pending: 'text-yellow-500',
  waiting: 'text-gray-400',
};

function TimelineItem({ log }: { log: ReviewLog }) {
  const Icon = STATUS_ICONS[log.status] || Circle;
  const color = STATUS_COLORS[log.status] || 'text-gray-400';

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <Icon className={`h-5 w-5 ${color}`} />
        <div className="mt-1 w-px flex-1 bg-gray-200" />
      </div>
      <div className="pb-4">
        <p className="text-sm font-medium text-gray-900">{log.name}</p>
        <p className="text-xs text-gray-500">
          {log.status_display} {log.done_by && `- ${log.done_by}`}
        </p>
        {log.reviewed_date && (
          <p className="text-xs text-gray-400">{log.reviewed_date}</p>
        )}
        {log.comment && (
          <p className="mt-1 rounded bg-gray-50 p-2 text-xs text-gray-600">
            {log.comment}
          </p>
        )}
      </div>
    </div>
  );
}

export function ApprovalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reviewId = id ? Number(id) : undefined;

  const { data: detail, isLoading } = useApprovalDetail(reviewId);
  const approveReview = useApproveReview();
  const rejectReview = useRejectReview();

  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);

  const isActing = approveReview.isPending || rejectReview.isPending;

  const handleAction = (type: 'approve' | 'reject') => {
    setActionType(type);
    setShowComment(true);
  };

  const handleConfirm = () => {
    if (!reviewId) return;
    if (actionType === 'approve') {
      approveReview.mutate({ id: reviewId, comment });
    } else {
      rejectReview.mutate({ id: reviewId, comment });
    }
    setShowComment(false);
    setComment('');
    setActionType(null);
  };

  if (isLoading || !detail) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const { review, all_reviews, review_logs } = detail;
  const rd = review.resource_data;
  const canReview = review.status === 'pending' || review.status === 'waiting';

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">簽核詳情</h1>
      </div>

      <div className="px-4 py-4">
        {/* Type badge */}
        <div className="mb-4 flex items-center gap-2">
          <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
            {review.model_display}
          </span>
          <span className="text-sm text-gray-500">{review.resource_name}</span>
        </div>

        {/* Resource Detail Card */}
        <div className="mb-4 space-y-3 rounded-xl bg-white p-4 shadow-sm">
          {rd.type === 'leave' && (
            <>
              <InfoRow label="申請人" value={rd.employee_name} />
              <InfoRow label="部門" value={rd.department_name} />
              <InfoRow label="假別" value={rd.holiday_type_name} />
              <InfoRow
                label="日期"
                value={
                  rd.start_day === rd.end_day
                    ? rd.start_day || '-'
                    : `${rd.start_day || ''} ~ ${rd.end_day || ''}`
                }
              />
              <InfoRow label="時數" value={formatWorkHours(rd.holiday_time_total)} highlight />
              <InfoRow label="代理人" value={rd.substitute_name || '-'} />
              {rd.report_note && <InfoRow label="事由" value={rd.report_note} />}
            </>
          )}
          {rd.type === 'overtime' && (
            <>
              <InfoRow label="申請人" value={rd.employee_name} />
              <InfoRow label="部門" value={rd.department_name} />
              <InfoRow label="加班類型" value={rd.overtime_type_name} />
              <InfoRow label="日期" value={rd.start_day || '-'} />
              <InfoRow label="時數" value={formatWorkHours(rd.duration_time)} highlight />
              {rd.desc && <InfoRow label="事由" value={rd.desc} />}
            </>
          )}
        </div>

        {/* Approval Tiers */}
        <div className="mb-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-500">簽核關卡</h2>
          <div className="space-y-2">
            {all_reviews.map((r) => {
              const StatusIcon = STATUS_ICONS[r.status] || Circle;
              const statusColor = STATUS_COLORS[r.status] || 'text-gray-400';
              return (
                <div
                  key={r.id}
                  className="flex items-center justify-between rounded-lg bg-white p-3 shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`h-4 w-4 ${statusColor}`} />
                    <span className="text-sm text-gray-900">{r.definition_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{r.status_display}</p>
                    <p className="text-xs text-gray-400">{r.reviewer_names.join(', ')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        {review_logs.length > 0 && (
          <div className="mb-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-500">簽核紀錄</h2>
            <div className="rounded-xl bg-white p-4 shadow-sm">
              {review_logs.map((log) => (
                <TimelineItem key={log.id} log={log} />
              ))}
            </div>
          </div>
        )}

        {/* Comment + Actions */}
        {canReview && review.can_review && (
          <>
            {showComment && (
              <div className="mb-3 space-y-2">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  placeholder="備註（選填）"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowComment(false);
                      setActionType(null);
                    }}
                    className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm text-gray-600"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isActing}
                    className={`flex-1 rounded-lg py-2.5 text-sm font-medium text-white disabled:opacity-40 ${
                      actionType === 'approve'
                        ? 'bg-green-500 hover:bg-green-600'
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {isActing ? '處理中...' : `確認${actionType === 'approve' ? '核准' : '駁回'}`}
                  </button>
                </div>
              </div>
            )}

            {!showComment && (
              <div className="flex gap-3 pb-4">
                <button
                  onClick={() => handleAction('reject')}
                  disabled={isActing}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-red-200 bg-white py-3 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
                >
                  <X className="h-4 w-4" />
                  駁回
                </button>
                <button
                  onClick={() => handleAction('approve')}
                  disabled={isActing}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-green-500 py-3 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-40"
                >
                  <Check className="h-4 w-4" />
                  核准
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <span
        className={`text-right text-sm ${
          highlight ? 'font-semibold text-indigo-600' : 'text-gray-900'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
